import { css } from '@emotion/react'
import { Cell, Grid, HFlow, Icon } from 'bold-ui'
import { Form, FormDebouncedValueSpy, RadioGroupField, SwitchField, TextField } from 'components/form'
import { PopperButton, PopperControls } from 'components/popper'
import { FormState } from 'final-form'
import createDecorator from 'final-form-calculate'
import { useFilterCounter } from 'hooks/useFilterCounter'
import { Dispatch, SetStateAction, useEffect, useMemo } from 'react'
import { metaPath } from 'util/metaPath'
import {
  AtividadeColetivaFilterFormModel,
  AtividadeColetivaFonteEnum,
} from 'view/atividade-coletiva/model-atividadeColetiva'

import { createAtividadeColetivaFilterFormCalculations } from '../calculator-atividadeColetiva'
import { AtividadeColetivaFilterPopperForm } from './AtividadeColetivaFilterPopperForm'
import { AtividadeColetivaSortDropdown } from './AtividadeColetivaSortDropdown'

const path = metaPath<AtividadeColetivaFilterFormModel>()

const decorator = createDecorator(...createAtividadeColetivaFilterFormCalculations(path))

interface AtividadeColetivaFilterFormProps {
  filter: AtividadeColetivaFilterFormModel
  onChangeFilter: Dispatch<SetStateAction<AtividadeColetivaFilterFormModel>>
}

export const AtividadeColetivaFilterForm = (props: AtividadeColetivaFilterFormProps) => {
  const { filter, onChangeFilter } = props
  const countAppliedFilter = useFilterCounter(filter, {
    isReuniao: false,
    isProcessado: filter.fonteRegistros === AtividadeColetivaFonteEnum.PEC,
    isSomenteRegistrosProprios: false,
    fonteRegistros: false,
    nomeCnsCpfProfissionalResponsavel: false,
    pageParams: false,
  })

  const isFonteOutros = useMemo(() => filter.fonteRegistros === AtividadeColetivaFonteEnum.OUTROS, [
    filter.fonteRegistros,
  ])

  useEffect(() => {
    if (!isFonteOutros && filter.origens && filter.origens.length > 0) {
      onChangeFilter((prev) => ({
        ...prev,
        origens: [],
      }))
    }
  }, [filter.origens, isFonteOutros, onChangeFilter])

  const renderPopper = (controls: PopperControls) => (
    <AtividadeColetivaFilterPopperForm filter={filter} onChangeFilter={onChangeFilter} onClose={controls.close} />
  )

  const handleChange = (formState: FormState<AtividadeColetivaFilterFormModel>) => {
    onChangeFilter({ ...formState.values })
  }

  const renderForm = () => {
    return (
      <Grid gapVertical={0.1}>
        <FormDebouncedValueSpy onChange={handleChange} />

        <Cell size={12} style={styles.fonte}>
          <RadioGroupField
            name={path.fonteRegistros}
            label='Fonte dos registros'
            options={[
              { value: AtividadeColetivaFonteEnum.PEC, label: 'Atividade coletiva' },
              {
                value: AtividadeColetivaFonteEnum.OUTROS,
                label: 'Outras fontes (CDS, app e-SUS AC ou sistemas terceiros)',
              },
            ]}
          />
        </Cell>

        <Cell size={4}>
          <TextField
            name={path.nomeCnsCpfProfissionalResponsavel}
            placeholder={
              filter.fonteRegistros === AtividadeColetivaFonteEnum.PEC
                ? 'Pesquise por nome, CPF ou CNS do responsável'
                : 'Pesquise por nome ou CNS do responsável'
            }
            icon='zoomOutline'
            style={styles.ellipsis}
          />
        </Cell>

        <Cell size={3}>
          <SwitchField
            style={styles.switch}
            name={path.isSomenteRegistrosProprios}
            label='Ver somente os meus registros'
          />
        </Cell>

        <Cell size={5}>
          <HFlow justifyContent='flex-end'>
            <AtividadeColetivaSortDropdown filter={filter} onChangeFilter={onChangeFilter} />
            <PopperButton
              kind='primary'
              size='small'
              skin='default'
              placement='bottom-end'
              renderPopper={renderPopper}
              focusOnOpen={false}
              dataTestId='PopperButton.Filtro'
            >
              <Icon icon='filterOutline' />
              {'Filtros' + (countAppliedFilter > 0 ? ` (${countAppliedFilter})` : '')}
            </PopperButton>
          </HFlow>
        </Cell>
      </Grid>
    )
  }

  return <Form<AtividadeColetivaFilterFormModel> initialValues={filter} render={renderForm} decorators={[decorator]} />
}

const styles = {
  ellipsis: css`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `,
  fonte: css`
    margin-bottom: 0.5rem;
  `,
  switch: css`
    padding-top: 0.25rem;
  `,
}
